'use client';

/**
 * **The parts a composite popover is built from, written once** (VALE-1505).
 *
 * `attribute-value-confirmation-editor.tsx` and `attribute-market-figure-editor.tsx`
 * each carried their own `Field` (a label over a control) and their own
 * `MoneyField` (a `$`-prefixed `useCurrencyInput`), byte-for-byte the same
 * component twice, and neither bound the composite-part tokens the address,
 * name, bank-account and principal-and-members popovers already share
 * (`ATTRIBUTE_COMPOSITE_PART*`), so those two panels were a different height
 * and rhythm from the four beside them. Two copies of one component is the
 * drift the root `CLAUDE.md` names; this is the one definition both read.
 *
 * `useCurrencyInput` is per field (a hook cannot run in a loop), which is why
 * the money part is a component rather than a helper.
 */

import { Input } from '@/components/ui/input';
import { useCurrencyInput } from '@/components/inventory/inventory-form-card/shared/currency-input';
import { cn } from '@/lib/utils';
import {
  ATTRIBUTE_COMPOSITE_PART,
  ATTRIBUTE_COMPOSITE_PART_CONTROL,
  ATTRIBUTE_COMPOSITE_PART_LABEL,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_VALUE_TEXT
} from './attribute-tokens';

/** One labelled part inside a composite popover: its label above its control. */
export function AttributeCompositeField({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={ATTRIBUTE_COMPOSITE_PART}>
      <span className={cn(ATTRIBUTE_COMPOSITE_PART_LABEL, ATTRIBUTE_MUTED_INK)}>
        {label}
      </span>
      {children}
    </div>
  );
}

/**
 * One money part: a `$` prefix, thousands separators, and the
 * raw-string-while-focused contract every other money field on the surface
 * has, bound to `useCurrencyInput` rather than re-typed, for the reasons the
 * grid's own `money` editor records.
 */
export function AttributeCompositeMoneyField({
  label,
  value,
  autoFocus = false,
  onChange
}: {
  label: string;
  /** The raw, unformatted figure. */
  value: string;
  autoFocus?: boolean;
  /** Receives the raw string as typed, never the formatted display value. */
  onChange: (raw: string) => void;
}) {
  const input = useCurrencyInput({ value });

  return (
    <AttributeCompositeField label={label}>
      <div className="relative flex items-center">
        <span
          aria-hidden
          className={cn(
            ATTRIBUTE_VALUE_TEXT,
            ATTRIBUTE_MUTED_INK,
            'pointer-events-none absolute left-2'
          )}
        >
          $
        </span>
        <Input
          autoFocus={autoFocus}
          aria-label={label}
          type="text"
          inputMode="decimal"
          value={input.displayValue}
          onFocus={(event) => {
            // Unformat, then select on the next frame so the selection survives the value swap.
            const element = event.currentTarget;
            // Typed before the frame came round: selecting then would swallow those keystrokes.
            delete element.dataset.typed;
            input.handleFocus();
            requestAnimationFrame(() => {
              if (
                document.activeElement === element &&
                !element.dataset.typed
              ) {
                element.select();
              }
            });
          }}
          onChange={(event) => {
            event.currentTarget.dataset.typed = '1';
            input.handleChange(event.target.value);
            onChange(event.target.value);
          }}
          onBlur={input.handleBlur}
          className={cn(ATTRIBUTE_COMPOSITE_PART_CONTROL, 'pl-5')}
        />
      </div>
    </AttributeCompositeField>
  );
}
